import type { IndexDef, ObjectiveDef, DomainDef, GlossaryEntry, LevelCode, TierDef } from '@/api/types/readiness'
import csaQri from './csa-qri-v1.json'
import hkmaQpi from './hkma-qpi.json'

// Indexes are versioned JSON documents loaded at startup (Engineering Note 1).
// Adding a jurisdiction = adding a file here. Nothing in the UI hard-codes the
// number of domains or objectives. Iteration 2 adds `scale`, `status:
// preliminary`, `aggregation`, `benchmarks`, `starting_set` and an inline
// flag per index (§11, §12, Note 7, Note 8).
const INDEXES: IndexDef[] = [csaQri as IndexDef, hkmaQpi as IndexDef]

export const DEFAULT_INDEX_ID = INDEXES.find((i) => i.status === 'active')?.id ?? INDEXES[0].id

export function listIndexes(): IndexDef[] {
  return INDEXES
}

export function getIndex(id: string): IndexDef {
  return INDEXES.find((i) => i.id === id) ?? INDEXES.find((i) => i.id === DEFAULT_INDEX_ID)!
}

export function allObjectives(index: IndexDef): Array<{ domain: DomainDef; objective: ObjectiveDef }> {
  return [...index.domains]
    .sort((a, b) => a.order - b.order)
    .flatMap((domain) => domain.objectives.map((objective) => ({ domain, objective })))
}

export function findObjective(index: IndexDef, code: string) {
  return allObjectives(index).find((x) => x.objective.code === code) ?? null
}

export function levelName(index: IndexDef, code: LevelCode): string {
  return index.level_scale.find((l) => l.code === code)?.name ?? code
}

export function glossaryFor(index: IndexDef, term: string): GlossaryEntry | undefined {
  const t = term.toLowerCase()
  return index.glossary.find((g) => g.term.toLowerCase() === t)
}

export function domainColor(domain: DomainDef, theme: 'light' | 'dark'): string {
  return theme === 'dark' ? domain.color_dark : domain.color_light
}

// Scale score_0_10 helpers (§11.3).
export function tiersOf(index: IndexDef, code: string): TierDef[] {
  return findObjective(index, code)?.objective.tiers ?? []
}
export function tierCount(index: IndexDef, code: string): number {
  return Math.max(2, tiersOf(index, code).length)
}
export function tierForScore(index: IndexDef, code: string, score: number | null): TierDef | null {
  if (score == null) return null
  const tiers = tiersOf(index, code)
  if (!tiers.length) return null
  const m = tiers.length
  // Nearest tier statement to the score.
  let best = tiers[0]
  let dist = Infinity
  tiers.forEach((t) => {
    const s = m <= 1 ? 10 : (10 * (t.n - 1)) / (m - 1)
    const d = Math.abs(s - score)
    if (d < dist) {
      dist = d
      best = t
    }
  })
  return best
}
export function sectorBenchmark(index: IndexDef, code: string): number | null {
  return findObjective(index, code)?.objective.benchmark?.sector ?? null
}
